import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import QuotationTable from './QuotationTable'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  Menu,
  MenuItem,
  Popover,
} from '@mui/material'
import { MedicalOrder } from '@services/api'
import { BillStatus, OrderDto, PrecotationDto } from '@services/dtos'
import {
  doValidatePrecotations,
  setPrecotation,
  doGetMemberSituation,
} from '@state/reducers/billingReducer'
import {
  doUpdateMedicalOrderAttendingDoctor,
  doUpdateMedicalOrdersEquipment,
  doUpdateOrder,
} from '@state/reducers/orderReducer'
import { UpTransition } from '../../../../components/animations'
import BillSummaryComponent from '../../../../components/billing/BillingSummaryComponent'
import { doGetCouverture } from '../../../../state/thunks/patientsThunk'
import { BillingDialog } from '../../billing/BillingDialog'
import { doUpdateCouverture } from '../../../../state/reducers/patientsReducer'
import { isBase64 } from '../../../../utils/dto.mapper'
import { CouvertureDto } from '../../../../common/interfaces'
import { getMyCashRegister } from '@state/reducers/cashRegisterReducer'
import { getInvoice } from '@state/reducers/invoiceReducer'
import useBillingWorkflow from '@hooks/useBilling'
import BillStatusDialog from './BillStatusDialog'
import { useSnackbar } from 'notistack'
import PatientInvoicesQuickPay from '@components/tables/PatientInvoicesQuickPay'
import { ReceiptText, Save, WalletCards } from 'lucide-react'

interface QuotationProps {
  order: OrderDto
  onMedicalOrderRemoved: (medicalOrder: MedicalOrder) => void
  toolbarContent?: React.ReactNode
}

const Quotation: React.FC<QuotationProps> = ({ order, toolbarContent }) => {
  const dispatch = useAppDispatch()

  // Local state
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

  const [billModeAnchor, setBillModeAnchor] = useState<HTMLElement | null>(null)
  const [openPatientPayment, setOpenPatientPayment] = useState(false)
  const billButtonRef = useRef<HTMLButtonElement>(null)
  const localRowsRef = useRef<PrecotationDto[]>([])
  const [showBillStatusDialog, setShowBillStatusDialog] = useState(false)
  const [saveCounter, setSaveCounter] = useState(0)
  const { enqueueSnackbar } = useSnackbar()
  // Redux state
  const { precotations, validation, couverture, memberSituation } =
    useAppSelector(({ billing, patients }) => ({
      precotations: billing.precotation,
      validation: billing.validation,
      couverture: patients.patientCouverture,
      memberSituation: billing.memberSituation,
    }))

  const selectedMedicalOrder = useMemo(
    () => order.medicalOrders[0],
    [order.medicalOrders],
  )
  const medicalOrdersSignature = useMemo(
    () =>
      order.medicalOrders
        .map((medicalOrder) => {
          const { exam } = medicalOrder
          return [
            medicalOrder.id,
            medicalOrder.plannedDate,
            exam?.code,
            exam?.cotation_ccam?.join(','),
            exam?.cotation_ngap?.join(','),
          ].join(':')
        })
        .join('|'),
    [order.medicalOrders],
  )

  const hasEmptyPrecotationValues = useMemo(() => {
    const source =
      localRowsRef.current.length > 0 ? localRowsRef.current : precotations
    return source.some((row) => {
      if (!row.cotation || row.cotation === '') return true
      if (row.prix_unitaire === null || row.prix_unitaire === undefined)
        return true
      if (row.prix_unitaire < 0) return true
      return false
    })
  }, [precotations])

  // Use shared billing workflow hook
  const {
    isLoading,
    isValidating,
    isCreatingBill,
    billUrl,
    codeSituationSelectorUrl,
    loadPrecotations,
    savePrecotations,
    handleBillModeClick,
    handleCreateBill,
    handleBillSuccess,
    handleInvoiceOnly,
    addPrecotationRow,
    deletePrecotationRow,
    setBillUrl,
    setCodeSituationSelectorUrl,
    closeSummary,
    billModesOptions,
  } = useBillingWorkflow({
    order,
    onInvoiceCreated: () => setOpenPatientPayment(true),
    onBillSuccess: async ({ invoices, autoSetPaid }) => {
      if (autoSetPaid) {
        await dispatch(
          doUpdateOrder({
            id: order.id,
            dto: { billStatus: BillStatus.PAID },
          }),
        )
        return
      }
      if (invoices.length > 0) {
        setOpenPatientPayment(true)
      }
    },
  })

  const handleBillStatusConfirm = async (status: BillStatus) => {
    try {
      await dispatch(
        doUpdateOrder({
          id: order.id,
          dto: { billStatus: status },
        }),
      ).unwrap()
    } catch (error) {
      enqueueSnackbar('Erreur lors de la mise à jour du statut', {
        variant: 'error',
      })

      throw error
    }
  }

  // Load data on mount
  useEffect(() => {
    // Load member situation
    if (order.attendingDoctor?.id) {
      dispatch(doGetMemberSituation(order.attendingDoctor.id))
    }

    // Load invoice
    dispatch(getInvoice({ orderId: order.id }))

    // Load cash register
    dispatch(getMyCashRegister())

    // Load precotations with prices
    loadPrecotations()

    // Cleanup on unmount
    return () => {
      dispatch(setPrecotation([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    order.id,
    order.attendingDoctor?.id,
    medicalOrdersSignature,
    dispatch,
  ])

  // Load couverture when patient changes
  useEffect(() => {
    if (order.patient.id) {
      dispatch(
        doGetCouverture({
          idPatient: order.patient.id,
          idVisit: order.visitId,
        }),
      )
    }
  }, [order.patient.id, order.visitId, dispatch])

  // Listen for code situation selector messages
  useEffect(() => {
    if (!codeSituationSelectorUrl) return

    const handleMessage = (event: MessageEvent) => {
      if (!isBase64(event.data)) return

      try {
        const decodedStr = atob(event.data)
        const obj = JSON.parse(decodedStr) as any

        if (obj.method?.name === 'SIT-gui-choix/situation') {
          if (obj.method.cancel) {
            setCodeSituationSelectorUrl('')
          } else if (obj.method.output?.selection && couverture) {
            const { id, ...rest } = couverture
            dispatch(
              doUpdateCouverture({
                id,
                dto: {
                  ...rest,
                  codeSituationAmo: obj.method.output.selection,
                } as CouvertureDto,
              }),
            ).then(() => {
              const rowsToValidate =
                localRowsRef.current.length > 0
                  ? localRowsRef.current
                  : precotations
              dispatch(doValidatePrecotations(rowsToValidate)).finally(() => {
                setCodeSituationSelectorUrl('')
              })
            })
          }
        }
      } catch (e) {
        console.error('Error parsing message:', e)
      }
    }

    window.addEventListener('message', handleMessage, false)
    return () => window.removeEventListener('message', handleMessage, false)
  }, [
    codeSituationSelectorUrl,
    couverture,
    precotations,
    dispatch,
    setCodeSituationSelectorUrl,
  ])

  // Handlers
  const handleAddQuotationRowForMedicalOrder = useCallback(
    (medicalOrderId: number) => {
      addPrecotationRow(medicalOrderId)
    },
    [addPrecotationRow],
  )

  const handleSave = useCallback(() => {
    if (order.deletedAt) {
      dispatch(doUpdateOrder({ id: order.id, dto: { deletedAt: null } }))
    }
    savePrecotations(localRowsRef.current).then(() => {
      setSaveCounter((c) => c + 1)
    })
  }, [order.deletedAt, order.id, dispatch, savePrecotations])

  const handleUpdateMedicalOrderEquipment = useCallback(
    async (medicalOrderIds: number[], medicalEquipmentId: number | null) => {
      try {
        await dispatch(
          doUpdateMedicalOrdersEquipment({
            orderId: order.id,
            medicalOrderIds,
            medicalEquipmentId,
          }),
        ).unwrap()
      } catch (e) {
        enqueueSnackbar('Erreur lors de la mise à jour de l’équipement', {
          variant: 'error',
        })
      }
    },
    [dispatch, order.id],
  )

  const handleUpdateMedicalOrderAttendingDoctor = useCallback(
    async (medicalOrderIds: number[], attendingDoctorId: number | null) => {
      try {
        await dispatch(
          doUpdateMedicalOrderAttendingDoctor({
            orderId: order.id,
            medicalOrderIds,
            attendingDoctorId,
          }),
        ).unwrap()
      } catch (e) {
        enqueueSnackbar('Erreur lors de la mise à jour du médecin traitant', {
          variant: 'error',
        })
      }
    },
    [dispatch, order.id],
  )

  const handleInvoiceOnlyClick = useCallback(async () => {
    setMenuAnchorEl(null)
    await handleInvoiceOnly()
    // Payment dialog opens via onInvoiceCreated callback
  }, [handleInvoiceOnly])

  const handleValidateMenuClick = useCallback(async () => {
    setMenuAnchorEl(null)
    setBillModeAnchor(billButtonRef.current)
  }, [])

  const onBillModeSelect = async (value: number) => {
    setBillModeAnchor(null)
    await handleBillModeClick(value, localRowsRef.current)
  }

  const isBillModeOpen = Boolean(billModeAnchor)

  const handlePaymentFormClose = useCallback(() => {
    setOpenPatientPayment(false)
    setShowBillStatusDialog(true)
  }, [])

  const billingButtonSx = {
    height: 34,
    borderRadius: '6px',
    px: 1.5,
    fontSize: { xs: '0.7rem', sm: '0.75rem' },
    fontWeight: 700,
    textTransform: 'uppercase',
    flexGrow: { xs: 1, sm: 0 },
  } as const

  const warningAlertSx = {
    alignItems: 'center',
    minHeight: 34,
    borderRadius: '6px',
    border: '1px solid #fed7aa',
    bgcolor: '#fff7ed',
    color: '#7c2d12',
    py: 0.5,
    px: 1,
    '& .MuiAlert-icon': {
      color: '#ea580c',
      mr: 0.75,
      py: 0,
    },
    '& .MuiAlert-message': {
      width: '100%',
      py: 0,
      minWidth: 0,
      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
      fontWeight: 600,
      lineHeight: 1.2,
    },
  } as const

  return (
    <div className="w-full min-h-0 flex-1 flex flex-col overflow-hidden">
      <div className="my-2 flex shrink-0 flex-col gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        {toolbarContent && (
          <div className="flex shrink-0 items-center gap-2">
            {toolbarContent}
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {validation && validation?.erreurs?.length !== 0 && (
            <>
              {validation.erreurs.map((err, index) => (
                <Alert
                  key={index}
                  severity="error"
                  sx={{
                    ...warningAlertSx,
                    borderColor: '#fecaca',
                    bgcolor: '#fef2f2',
                    color: '#7f1d1d',
                    '& .MuiAlert-icon': {
                      color: '#dc2626',
                      mr: 1.25,
                    },
                  }}
                >
                  {err.messageErreur}
                </Alert>
              ))}
            </>
          )}

          {!memberSituation && (
            <Alert severity="warning" sx={warningAlertSx}>
              <span title="Il est nécessaire de mettre à jour la situation du praticien pour procéder à la facturation.">
                Situation praticien à mettre à jour
              </span>
            </Alert>
          )}
          {!order.patient.isForeigner && !couverture && (
            <Alert severity="warning" sx={warningAlertSx}>
              <span title="Il est nécessaire de mettre à jour la couverture de ce patient pour procéder à la facturation.">
                Couverture patient à mettre à jour
              </span>
            </Alert>
          )}
        </div>

        <div className="flex w-full flex-row flex-wrap gap-2 xl:w-auto xl:justify-end">
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={isValidating}
            size="small"
            startIcon={
              isValidating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Save size={16} />
              )
            }
            sx={{
              ...billingButtonSx,
              color: '#374151',
              borderColor: '#d1d5db',
              transition: 'all 0.3s',
              '&:hover': {
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb',
              },
            }}
          >
            Sauvegarder
          </Button>

          {order.patient.isForeigner ? (
            <Button
              variant="contained"
              disabled={!memberSituation || precotations.length === 0}
              onClick={handleInvoiceOnlyClick}
              size="small"
              startIcon={<WalletCards size={16} />}
              sx={{
                ...billingButtonSx,
                bgcolor: '#7c3aed',
                boxShadow: '0 8px 18px rgba(124, 58, 237, 0.18)',
                '&:hover': {
                  bgcolor: '#6d28d9',
                  boxShadow: '0 10px 20px rgba(124, 58, 237, 0.22)',
                },
              }}
            >
              Règlement patient
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                ref={billButtonRef}
                disabled={
                  !memberSituation ||
                  precotations.length === 0 ||
                  hasEmptyPrecotationValues
                }
                onClick={(event) =>
                  setMenuAnchorEl(event.currentTarget as HTMLElement)
                }
                size="small"
                startIcon={
                  isValidating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <ReceiptText size={16} />
                  )
                }
                sx={{
                  ...billingButtonSx,
                  bgcolor: '#7c3aed',
                  boxShadow: '0 8px 18px rgba(124, 58, 237, 0.18)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: '#6d28d9',
                    boxShadow: '0 10px 20px rgba(124, 58, 237, 0.22)',
                  },
                }}
              >
                Facturer
              </Button>

              <Menu
                anchorEl={menuAnchorEl}
                open={!!menuAnchorEl}
                onClose={() => setMenuAnchorEl(null)}
              >
                <MenuItem onClick={handleInvoiceOnlyClick}>
                  Paiement en amont
                </MenuItem>
                <MenuItem onClick={handleValidateMenuClick}>
                  Facturation finale
                </MenuItem>
              </Menu>
            </>
          )}
        </div>
      </div>

      {/* Bill mode selector popover */}
      <Popover
        open={isBillModeOpen}
        anchorEl={billModeAnchor}
        onClose={() => setBillModeAnchor(null)}
      >
        <List>
          {billModesOptions.map((item) => (
            <ListItem key={item.value}>
              <Button
                disabled={item.disabled}
                onClick={() => onBillModeSelect(item.value)}
                variant="text"
              >
                {item.label}
              </Button>
            </ListItem>
          ))}
        </List>
      </Popover>

      <QuotationTable
        orderId={order.id}
        isLoading={isLoading}
        rows={precotations}
        onAddRowForMedicalOrder={handleAddQuotationRowForMedicalOrder}
        onRemoveRow={deletePrecotationRow}
        medicalOrderId={selectedMedicalOrder?.id}
        registeredAt={order.registeredAt}
        medicalOrders={order.medicalOrders}
        onUpdateMedicalOrderEquipment={handleUpdateMedicalOrderEquipment}
        localRowsRef={localRowsRef}
        saveCounter={saveCounter}
        onUpdateMedicalOrderAttendingDoctor={
          handleUpdateMedicalOrderAttendingDoctor
        }
      />

      <Dialog
        fullScreen
        sx={{
          '& .MuiDialog-paper': { minHeight: { xs: '100vh', sm: '80vh' } },
        }}
        open={!!validation}
        TransitionComponent={UpTransition}
      >
        <DialogContent>
          {validation?.visite && <BillSummaryComponent data={validation} />}
          <DialogActions>
            <Button variant="outlined" onClick={closeSummary}>
              Fermer
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateBill}
              disabled={isCreatingBill}
            >
              {isCreatingBill && (
                <CircularProgress
                  size={18}
                  style={{ color: 'gray', marginRight: '8px' }}
                />
              )}
              Créer la facture
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Code situation selector iframe dialog */}
      <Dialog
        open={!!codeSituationSelectorUrl}
        onClose={() => setCodeSituationSelectorUrl('')}
        fullScreen
        TransitionComponent={UpTransition}
      >
        <DialogContent>
          <iframe src={codeSituationSelectorUrl} width="100%" height="100%" />
        </DialogContent>
      </Dialog>

      {/* Billing iframe dialog */}
      <BillingDialog
        isOpen={!!billUrl}
        onClose={() => setBillUrl('')}
        billUrl={billUrl}
        onSuccess={handleBillSuccess}
      />

      <BillStatusDialog
        open={showBillStatusDialog}
        onClose={() => setShowBillStatusDialog(false)}
        onConfirm={handleBillStatusConfirm}
      />

      {/* Patient payment form dialog */}
      <Dialog
        open={openPatientPayment}
        onClose={() => {}}
        TransitionComponent={UpTransition}
        maxWidth="lg"
        fullScreen
      >
        {openPatientPayment && (
          <DialogContent>
            <div className="flex justify-end mb-2">
              <Button
                variant="contained"
                onClick={() => handlePaymentFormClose()}
              >
                Terminer
              </Button>
            </div>
            <PatientInvoicesQuickPay patientId={order.patient.id} />
          </DialogContent>
        )}
      </Dialog>
    </div>
  )
}

export default Quotation
